"use client";

import { cn } from "@gettactic/helpers";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle
} from "../../primitives/Dialog/Dialog";

export interface PreviewImage {
  url: string;
  filename?: string;
}

export interface ImagePreviewDialogProps {
  /** The set the viewer can page through. */
  images: PreviewImage[];
  /** Index of the image on screen; `null` keeps the dialog closed. */
  index: number | null;
  /** Called when the viewer pages to another image. */
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

const controlClasses =
  "flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-neutral-700 shadow-md transition-colors hover:bg-white hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";

/**
 * Full-size viewer for a set of images, with paging when there is more than
 * one. Left/right arrows and the keyboard move between them; Escape closes.
 * Generic on purpose — any surface holding a list of image URLs can use it.
 */
export const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({
  images,
  index,
  onIndexChange,
  onClose
}) => {
  const isOpen = index !== null && index >= 0 && index < images.length;
  const current = isOpen ? images[index] : undefined;
  const hasPaging = images.length > 1;

  const step = (delta: number) => {
    if (index === null || !hasPaging) {
      return;
    }
    // Wrap around so paging never dead-ends on the first or last image.
    onIndexChange((index + delta + images.length) % images.length);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        // The paging controls live on the backdrop, beside the card, so they
        // stay legible whatever the photo looks like. That needs the content to
        // stop clipping, and a gutter wide enough to hold them at any width.
        className="w-[calc(100%-7rem)] max-w-4xl overflow-visible p-0"
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "ArrowRight") {
            event.preventDefault();
            step(1);
          }
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            step(-1);
          }
        }}
        // Sits above the card on the backdrop, for the same reason as the paging
        // arrows: a control laid over the picture disappears on a light one.
        customCloseButton={
          <button
            type="button"
            onClick={onClose}
            className={cn(controlClasses, "absolute -top-11 right-0")}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        }
      >
        {current ? (
          <>
            <div className="overflow-hidden sm:rounded-brand">
              <div className="flex min-h-[240px] items-center justify-center bg-neutral-900">
                <img
                  src={current.url}
                  alt={current.filename || "Attachment"}
                  className="max-h-[70vh] w-auto max-w-full object-contain"
                />
              </div>

              <div className="flex items-center justify-between gap-4 bg-white px-6 py-4">
                <div className="min-w-0">
                  <DialogTitle className="truncate font-medium text-neutral-900 text-sm">
                    {current.filename || "Attachment"}
                  </DialogTitle>
                  {hasPaging ? (
                    <p className="mt-0.5 text-2xs text-neutral-500">
                      {(index ?? 0) + 1} of {images.length}
                    </p>
                  ) : null}
                </div>
                <a
                  href={current.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex shrink-0 items-center gap-1.5 rounded-brand border border-neutral-200 px-3 py-1.5 font-medium text-neutral-700 text-xs transition-colors hover:border-neutral-300 hover:text-neutral-900"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </a>
              </div>
            </div>

            {hasPaging ? (
              <>
                <button
                  type="button"
                  onClick={() => step(-1)}
                  aria-label="Previous image"
                  className={cn(
                    controlClasses,
                    "absolute top-1/2 -left-11 -translate-y-1/2"
                  )}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => step(1)}
                  aria-label="Next image"
                  className={cn(
                    controlClasses,
                    "absolute top-1/2 -right-11 -translate-y-1/2"
                  )}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
